import type {
  GetV5DevicesQueryParamsSortEnumKey,
  WorkspaceResponseV2,
} from '@emma/api_client/types';
import { Button, Tooltip } from '@lego/klik-ui';
import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useDevices } from '@/hooks/device';
import { useCanOperate, useCanWrite } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { deviceTableColumns } from '../common/table/column_definitions/Device';
import { deviceEmptyMessage } from '../common/table/deviceEmptyMessage';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { RegisterDeviceButton } from '../device/RegisterDeviceButton';
import { BatchUpdateCoreVersionModal } from '../fleet/BatchUpdateCoreVersionModal';

type Props = {
  workspace: WorkspaceResponseV2;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  name: 'nameLike',
  macAddress: 'macAddressLike',
  reportedIp: 'reportedIpLike',
  workspace: 'workspaceIdIn',
  isConnected: 'isConnected',
  fleet: 'fleetIdIn',
  workcenter: 'workcenterIdLike',
  os: 'osNameLike',
  createdAt: ['createdAfter', 'createdBefore'],
  reportedCoreVersion: 'reportedCoreVersionLike',
  state: 'stateIn',
};

const WorkspaceDeviceTableContent = ({ workspace }: Props) => {
  const canWrite = useCanWrite(workspace);
  const canOperate = useCanOperate(workspace);
  const [isCoreVersionModalOpen, setIsCoreVersionModalOpen] = useState(false);
  const { pagination, sorting, filters, rowSelection, setRowSelection } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);
  const activeFilters = buildFilterParams(debouncedFilters, FILTERS);

  const selectedDeviceIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map(Number),
    [rowSelection]
  );

  const queryParams = {
    ...serverTableParams<GetV5DevicesQueryParamsSortEnumKey>(pagination, sorting),
    ...activeFilters,
    workspaceIdIn: [workspace.id],
  };

  const { data, isLoading, mutate, isValidating } = useDevices(queryParams);
  const devices = useMemo(() => data?.devices ?? [], [data?.devices]);

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <>
      <Table
        addButton={
          canOperate
            ? (buttonProps) => (
                <RegisterDeviceButton
                  {...buttonProps}
                  onCreated={() => void mutate()}
                  workspaceId={workspace.id}
                />
              )
            : undefined
        }
        columns={deviceTableColumns}
        data={devices}
        emptyMessage={deviceEmptyMessage(
          data?.itemCount,
          'No devices in this workspace',
          Object.keys(activeFilters).length > 0
        )}
        enableRowSelection={canWrite}
        getRowCanSelect={(row) => row.original.state === 'PROVISIONED'}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        rowLink={({ id }) => paths.deviceById(id)}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Devices"
        toolbarAddon={
          canWrite ? (
            <Tooltip
              label={
                selectedDeviceIds.length === 0
                  ? 'Please select a device to proceed.'
                  : 'Batch action to be performed on selected devices.'
              }
              placement="left"
            >
              <Button
                isDisabled={selectedDeviceIds.length === 0}
                onClick={() => setIsCoreVersionModalOpen(true)}
                size="sm"
                variant="outline"
              >
                Update Core Version
              </Button>
            </Tooltip>
          ) : undefined
        }
      />
      {canWrite && (
        <BatchUpdateCoreVersionModal
          deviceCount={selectedDeviceIds.length}
          deviceIds={selectedDeviceIds}
          isOpen={isCoreVersionModalOpen}
          onClose={() => setIsCoreVersionModalOpen(false)}
          onSuccess={() => {
            setRowSelection({});
            void mutate();
          }}
          workspaceId={workspace.id}
        />
      )}
    </>
  );
};

export const WorkspaceDeviceTable = (props: Props) => (
  <TableStoreProvider
    initialColumnVisibility={{
      workspace: false,
    }}
    initialSorting={[{ desc: true, id: 'id' }]}
    name="WorkspaceDevices"
  >
    <WorkspaceDeviceTableContent {...props} />
  </TableStoreProvider>
);
